import { useEffect, useRef, useState } from 'react'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query'
import { Copy, Check, RefreshCw } from 'lucide-react'
import { api } from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'

type SuggestionStatus = 'queued' | 'completed' | 'failed'

interface ReviewSuggestion {
  status: SuggestionStatus
  version: number
  review_text?: string
  error?: string | null
}

interface ReviewSuggestionQueryData {
  suggestion: ReviewSuggestion | null
}

interface SuggestedReviewBlockProps {
  reservationId: string
  guestName?: string | null
  initialSuggestion?: ReviewSuggestion | null
}

// Suggestions are generated proactively by the review-window-open webhook
// (see admin-api's review-email-webhook.js), so most of the time this query
// lands on a "completed" suggestion right away. "queued" only shows up in
// the narrow window right after the webhook fires, or right after a manual
// Regenerate click — poll while in that state, then stop.
const POLL_INTERVAL_MS = 3000
// A null suggestion is ambiguous: it can mean "never generated" (show the
// manual Generate button), or it can mean the webhook already enqueued a
// job that the queue consumer hasn't picked up yet (no "queued" placeholder
// exists in KV until it does) — polling only on status === 'queued' would
// never observe that second case without a reload. Poll a null result a
// few times too, capped so a reservation that genuinely has no suggestion
// doesn't poll forever.
const MAX_NULL_POLL_ATTEMPTS = 3
// A genuinely "queued" (actively generating) or stale-post-regeneration
// suggestion previously polled unconditionally for the mounted lifetime of
// the block — for a Checking-out-today row (always mounted, not
// collapsible) a permanently stuck job would generate API traffic forever
// (Codex review on this PR). Bounded higher than MAX_NULL_POLL_ATTEMPTS
// since real generation (an actual OpenAI call) can genuinely take longer
// than the few seconds the "is this even started yet" ambiguity window
// needs. 10 attempts (30s) is still generous headroom for an OpenAI call
// that normally completes in a few seconds.
const MAX_ACTIVE_GENERATION_POLL_ATTEMPTS = 10

// dataUpdateCount only advances on a successful fetch — a failing endpoint
// leaves it frozen, so a budget based on it alone never runs out and polls
// forever against something that's down (Codex review on this PR). Combine
// it with errorUpdateCount so every settled fetch, success or failure,
// counts toward the budget.
function getFetchAttemptCount(
  queryState: { dataUpdateCount?: number; errorUpdateCount?: number } | undefined
) {
  return (queryState?.dataUpdateCount ?? 0) + (queryState?.errorUpdateCount ?? 0)
}

// A suggestion is still generating either because it's explicitly `queued`,
// or because we're waiting for a version a triggered Generate/Regenerate
// promised (see targetVersionQueryKey below) and haven't seen it yet — the
// queue consumer hasn't written that version's row when `suggestion` is
// null, or the row is still on an older version that hasn't caught up.
function isSuggestionStillGenerating(
  suggestion: ReviewSuggestion | null | undefined,
  targetVersion: number
) {
  if (suggestion?.status === 'queued') return true
  return targetVersion > 0 && (!suggestion || suggestion.version < targetVersion)
}

// Whether `incoming` (e.g. from a digest refresh) represents newer
// information than `cached` (already in the query cache) for the same
// reservation, and should replace it. A strictly higher version always
// wins. At the *same* version, only a queued cached entry can still be
// superseded — by its own completion — since a completed cached entry is
// never regressed back to queued by a stale same-version snapshot (Codex
// review on this PR: comparing versions alone let a digest that hadn't
// caught up yet flip a completed draft back to "Generating…").
function isFresherSuggestion(
  incoming: ReviewSuggestion | null | undefined,
  cached: ReviewSuggestion | null | undefined
) {
  const incomingVersion = incoming?.version ?? -1
  const cachedVersion = cached?.version ?? -1
  if (incomingVersion !== cachedVersion) return incomingVersion > cachedVersion
  return cached?.status === 'queued' && incoming?.status !== 'queued'
}

// Whichever of `incoming` or `cached` isFresherSuggestion prefers — used to
// decide "is this reservation still generating" against the best information
// actually available, not just whichever of the two happens to be checked
// first. Without this, a stale `queued` prop can win a still-generating
// check even when the cache already holds that same generation's completed
// result (Codex review on this PR).
function effectiveSuggestion(
  incoming: ReviewSuggestion | null | undefined,
  cached: ReviewSuggestion | null | undefined
) {
  return isFresherSuggestion(incoming, cached) ? incoming : cached
}

// A digest-embedded `queued` suggestion at version N is the same kind of
// promise a locally triggered Generate/Regenerate is, but only the latter
// records a target version (see targetVersionQueryKey below) — a queued
// suggestion arriving via the digest never did. Without a recorded target,
// the first per-reservation GET this row's own polling kicks off can still
// observe the previous, not-yet-superseded "completed" version (the same
// Workers KV eventual-consistency gap the regenerate case already guards
// against) and isSuggestionStillGenerating would treat that stale response
// as done, settling the row on the old draft and never polling for N (Codex
// review on this PR). Recording N here closes that gap the same way. Never
// regresses an already-higher target — e.g. a local regenerate already
// promised a newer version than this (possibly stale) digest snapshot knows
// about.
// Returns whether this call actually raised the recorded target version, so
// callers can tell a genuine new-generation promise apart from a no-op
// (already-at-or-past-target) call — used to reset the polling budget only
// when a generation actually just started (Codex review on this PR).
function recordEmbeddedTargetVersion(
  queryClient: QueryClient,
  targetVersionQueryKey: QueryKey,
  suggestion: ReviewSuggestion | null | undefined
) {
  if (suggestion?.status !== 'queued' || suggestion.version == null) return false
  let bumped = false
  queryClient.setQueryData<number>(targetVersionQueryKey, current => {
    const next = Math.max(current ?? 0, suggestion.version)
    bumped = next > (current ?? 0)
    return next
  })
  return bumped
}

interface CopyButtonProps {
  text: string
  label: string
}

function CopyButton({ text, label }: CopyButtonProps) {
  const [copied, setCopied] = useState(false)
  const { toast } = useToast()

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      toast({ title: 'Copied', description: `${label} copied to clipboard` })
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast({
        title: 'Copy failed',
        description: `Failed to copy ${label.toLowerCase()}`,
        variant: 'destructive',
      })
    }
  }

  return (
    <Button size="sm" variant="outline" onClick={handleCopy}>
      {copied ? (
        <Check className="mr-1.5 h-3.5 w-3.5 text-mint" />
      ) : (
        <Copy className="mr-1.5 h-3.5 w-3.5" />
      )}
      {copied ? 'Copied' : label}
    </Button>
  )
}

export function SuggestedReviewBlock({
  reservationId,
  guestName,
  initialSuggestion,
}: SuggestedReviewBlockProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const queryKey = ['review-suggestion', reservationId]
  const targetVersionQueryKey = ['review-suggestion-target-version', reservationId]
  // An embedded suggestion still generating needs the query enabled from the
  // start so refetchInterval below can pick up its completion — only a
  // settled (completed or null) embedded suggestion is safe to skip fetching
  // entirely (Codex review on this PR). Also checks the query cache, not
  // just the prop: a collapsible row remounts on collapse/reopen against
  // whatever (possibly stale/older) initialSuggestion the parent's digest
  // still holds, while the cache can already contain a newer queued draft
  // from a Generate/Regenerate that hasn't settled yet (Codex review on
  // this PR) — either source reporting "still generating" is enough to poll.
  const [shouldFetch, setShouldFetch] = useState(() => {
    if (initialSuggestion === undefined) return true
    const cachedSuggestion =
      queryClient.getQueryData<ReviewSuggestionQueryData>(queryKey)?.suggestion
    const targetVersion = queryClient.getQueryData<number>(targetVersionQueryKey) ?? 0
    return isSuggestionStillGenerating(
      effectiveSuggestion(initialSuggestion, cachedSuggestion),
      targetVersion
    )
  })
  // An always-mounted row (Checking-out-today) never remounts, so a digest
  // refresh that transitions its embedded suggestion to "generating" (e.g.
  // someone else just triggered it) only reaches this component as a new
  // initialSuggestion prop, not a fresh mount — shouldFetch otherwise only
  // reflects mount-time state (Codex review on this PR). Adjusted here
  // during render (comparing against the previous render's prop via state,
  // not a ref — refs can't be read during render under this repo's React
  // Compiler rules), per React's "adjusting state when a prop changes"
  // pattern, not in an effect: setState-in-effect causes an extra commit
  // and trips react-hooks/set-state-in-effect.
  const [prevInitialSuggestion, setPrevInitialSuggestion] = useState(initialSuggestion)
  if (initialSuggestion !== prevInitialSuggestion) {
    setPrevInitialSuggestion(initialSuggestion)
    if (!shouldFetch) {
      // Mirrors the mount-time initializer above: undefined unconditionally
      // means "fetch it ourselves" (this row was never embedded a digest
      // suggestion at all), not just the "still generating" check that
      // applies once a suggestion value is actually present (Codex review
      // on this PR — a digest response that stops embedding the field
      // otherwise left the query disabled, stuck on stale cached data).
      const targetVersion = queryClient.getQueryData<number>(targetVersionQueryKey) ?? 0
      const cachedSuggestion =
        queryClient.getQueryData<ReviewSuggestionQueryData>(queryKey)?.suggestion
      if (
        initialSuggestion === undefined ||
        isSuggestionStillGenerating(
          effectiveSuggestion(initialSuggestion, cachedSuggestion),
          targetVersion
        )
      ) {
        setShouldFetch(true)
      }
    }
  }
  useEffect(() => {
    if (initialSuggestion === undefined) return
    // Reconciles the cache from the digest's own view any time it changes —
    // deliberately not gated on shouldFetch. If direct polling exhausts its
    // budget while still queued and a later digest refresh reports the
    // completed version, that's newer information this row should still
    // pick up even though it's no longer fetching for itself (Codex review
    // on this PR). A collapsible row unmounts and remounts on
    // collapse/reopen too, which re-runs this effect against whatever
    // `initialSuggestion` the parent's digest data still holds — that
    // digest may not have refetched since a Generate/Regenerate already
    // wrote a newer suggestion into this same cache entry. Only seed when
    // the cache is empty or actually behind, so a stale embedded value
    // can't clobber a fresher one either way (Codex review on this PR).
    // This is a genuine external-system sync (the query cache), unlike the
    // shouldFetch adjustment above, so it stays in an effect.
    queryClient.setQueryData<ReviewSuggestionQueryData>(
      ['review-suggestion', reservationId],
      oldData => {
        if (oldData !== undefined && !isFresherSuggestion(initialSuggestion, oldData?.suggestion)) {
          return oldData
        }
        return { suggestion: initialSuggestion ?? null }
      }
    )
  }, [initialSuggestion, queryClient, reservationId])
  // This key is only ever read/written imperatively (getQueryData/
  // setQueryData below) — no useQuery ever observes it, so its observer
  // count is always 0 and it would otherwise be eligible for garbage
  // collection under the app's configured gcTime (or immediately, under a
  // test client's gcTime: 0) almost as soon as it's set, well before the
  // next poll could read it back. Pin it to never expire so it reliably
  // survives an unmount/remount the same way the main query's own cache
  // entry does for pollBaselineRef above.
  queryClient.setQueryDefaults(targetVersionQueryKey, { gcTime: Infinity })
  // dataUpdateCount is a lifetime counter for this query cache entry, not
  // "attempts since the current generation started" — without tracking a
  // baseline, a reservation whose initial poll budget was already
  // exhausted before the host clicks Generate would never poll the
  // post-enqueue response either, missing the completed draft without a
  // reload (Codex review on this PR). Reset the baseline to the count at
  // the moment generation is (re)triggered, so the budget always covers
  // the fetches that follow it. Also initialized from the cache (not a
  // hardcoded 0) so a block that unmounts and remounts within the cache's
  // lifetime — e.g. a Pending-reviews row collapsed and reopened — starts
  // its own bounded polling window instead of inheriting an
  // already-exhausted dataUpdateCount from before (Codex review, round 2
  // on this PR): useRef's initial value is only used on the very first
  // render, so this doesn't re-read on every render. Shared between the
  // null-poll and active-generation budgets below — both represent "how
  // many fetches have happened since we last had a reason to wait".
  const pollBaselineRef = useRef(getFetchAttemptCount(queryClient.getQueryState(queryKey)))
  useEffect(() => {
    if (initialSuggestion === undefined) return
    // Record any embedded queued version as a polling target before this
    // row's own fetch (triggered by the shouldFetch flip above, in the same
    // commit) can observe a stale "completed" response and settle early on
    // it (Codex review on this PR) — this effect runs before useQuery's own
    // mount/enable effect below since hooks commit in declaration order.
    const targetBumped = recordEmbeddedTargetVersion(
      queryClient,
      ['review-suggestion-target-version', reservationId],
      initialSuggestion
    )
    // A newer embedded queued version raises the target this row is now
    // waiting for, but dataUpdateCount/errorUpdateCount are lifetime
    // counters for the cache entry, not "attempts since this generation
    // started" — without resetting the baseline here too, a fresh
    // generation reported by a later digest refresh on an always-mounted
    // (never remounts) row inherits every fetch already spent on the
    // PREVIOUS generation, and can start out already exhausted (or with a
    // shrunk window) even though this generation hasn't been polled at all
    // yet (Codex review on this PR). Mirrors the reset regenerateMutation's
    // onSuccess already does for the locally-triggered case, below.
    if (targetBumped) {
      pollBaselineRef.current = getFetchAttemptCount(
        queryClient.getQueryState(['review-suggestion', reservationId])
      )
    }
  }, [initialSuggestion, queryClient, reservationId])
  // Set from the regenerate response's own suggestion.version on success —
  // the backend pre-creates that version's "queued" placeholder before
  // returning (see handleRegenerateReviewSuggestion), but Workers KV is
  // only eventually consistent, so the very next GET (triggered by
  // invalidateQueries below) can still return the previous, already-
  // "completed" version for a beat. Without tracking the version we're
  // actually waiting for, the poll below would see a "completed" status,
  // treat it as done, and settle back on the stale pre-regeneration draft
  // instead of the new one (Codex review on this PR).
  //
  // Stored in the query cache (via targetVersionQueryKey below), not a
  // plain useRef — a Pending-reviews row is collapsible, and a useRef
  // resets to its initial value on remount. If a host collapsed the row
  // right after clicking Regenerate and reopened it before KV caught up,
  // the fresh mount's ref would read 0, the "waiting for target version"
  // check would go false, and the UI would settle permanently on the old
  // draft since nothing would notice the real version ever arrived (Codex
  // review on this PR). queryClient's cache, unlike a ref, survives that
  // remount for as long as the query's own gcTime does.

  const query = useQuery<ReviewSuggestionQueryData>({
    queryKey,
    queryFn: async () => {
      const data = await api.getReviewSuggestion(reservationId)
      // TanStack Query writes a successful queryFn's return value into the
      // cache unconditionally, bypassing the isFresherSuggestion guard the
      // digest-reconciliation effect above already applies to its own
      // writes. A direct poll that was already in flight can resolve after
      // that effect wrote a fresher (e.g. newly completed) result into this
      // same cache entry, and without this guard its stale response would
      // regress the entry right back (Codex review on this PR). Returning
      // the cached value instead of the stale fetch result keeps the same
      // "never regress" protection on this write path too.
      const cached = queryClient.getQueryData<ReviewSuggestionQueryData>(queryKey)
      if (cached !== undefined && !isFresherSuggestion(data?.suggestion, cached?.suggestion)) {
        return cached
      }
      return data
    },
    enabled: !!reservationId && shouldFetch,
    initialData:
      initialSuggestion === undefined ? undefined : { suggestion: initialSuggestion ?? null },
    // Consecutive polls of a "queued" (or stale pre-regeneration) draft
    // return structurally-identical data, so TanStack Query's default
    // tracked-properties optimization — which only re-renders the
    // component when a property it actually read during the last render
    // changed — sees no change and skips re-rendering. That silently broke
    // the exhausted-poll-budget UI below: dataUpdateCount kept advancing
    // internally (refetchInterval saw it and correctly stopped polling),
    // but the component itself never got a render to notice and show the
    // "taking longer than expected" fallback. Opt this query out of that
    // optimization so every fetch resolution re-renders.
    notifyOnChangeProps: 'all',
    // TanStack Query v5 passes the Query instance here, not the resolved
    // data directly — read it off query.state.data.
    refetchInterval: query => {
      const suggestion = query.state.data?.suggestion
      // A null result while we're waiting for a version we just triggered
      // (Generate or Regenerate) is the *same* "still generating" case as a
      // stale completed draft — the queue consumer hasn't written its
      // placeholder yet — so it belongs on the same bounded budget, not the
      // shorter, unrelated MAX_NULL_POLL_ATTEMPTS window that exists for the
      // "never generated, and nothing was ever triggered" ambiguity (Codex
      // review on this PR: a null response right after a triggered
      // generation was falling through to the short window and expiring
      // before the job could realistically finish). isSuggestionStillGenerating
      // captures both cases.
      const targetVersion = queryClient.getQueryData<number>(targetVersionQueryKey) ?? 0
      // Counts fetches that resolved to an error too, not just successful
      // ones — dataUpdateCount alone never advances while the endpoint is
      // failing, so a budget based on it alone never runs out and polling
      // continues indefinitely against a dead endpoint (Codex review on
      // this PR).
      const attemptsSinceGeneration = getFetchAttemptCount(query.state) - pollBaselineRef.current

      if (isSuggestionStillGenerating(suggestion, targetVersion)) {
        return attemptsSinceGeneration < MAX_ACTIVE_GENERATION_POLL_ATTEMPTS
          ? POLL_INTERVAL_MS
          : false
      }
      if (!suggestion && attemptsSinceGeneration < MAX_NULL_POLL_ATTEMPTS) {
        return POLL_INTERVAL_MS
      }
      return false
    },
  })

  const regenerateMutation = useMutation({
    mutationFn: () => api.regenerateReviewSuggestion(reservationId),
    onSuccess: (data: { suggestion?: ReviewSuggestion } | undefined) => {
      if (data?.suggestion) {
        // A digest refresh can install a fresher (e.g. already-completed)
        // result into this same cache entry while this POST is still in
        // flight -- a fast enough generation can be seen by a separate
        // digest poll before this request's own response makes it back
        // (Codex review on this PR). Without the same freshness guard the
        // digest-reconciliation and direct-fetch write paths already use,
        // this unconditional write would regress that fresher result right
        // back with the POST's own stale response.
        queryClient.setQueryData<ReviewSuggestionQueryData>(queryKey, oldData => {
          if (oldData !== undefined && !isFresherSuggestion(data.suggestion, oldData?.suggestion)) {
            return oldData
          }
          return { suggestion: data.suggestion as ReviewSuggestion }
        })
      }
      queryClient.setQueryData<number>(targetVersionQueryKey, data?.suggestion?.version ?? 0)
      // Read straight from the cache rather than the `query` object closed
      // over by this render — queryClient isn't render-dependent, so this
      // is guaranteed current regardless of when this async callback fires.
      pollBaselineRef.current = getFetchAttemptCount(queryClient.getQueryState(queryKey))
      setShouldFetch(true)
      queryClient.invalidateQueries({ queryKey })
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : undefined
      toast({
        title: 'Regeneration failed',
        description: message || 'Something went wrong.',
        variant: 'destructive',
      })
    },
  })

  if (!reservationId) return null

  const suggestion = query.data?.suggestion

  if (query.isLoading) {
    return (
      <div className="rounded-lg border border-border bg-surface2/50 px-4 py-2.5 text-xs text-textMuted">
        Loading suggested review…
      </div>
    )
  }

  if (query.isError) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-danger/30 bg-danger/5 px-4 py-2.5">
        <span className="text-xs text-danger">Couldn&apos;t load the suggested review.</span>
        <Button size="sm" variant="outline" onClick={() => query.refetch()}>
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
          Retry
        </Button>
      </div>
    )
  }

  // A null result, or a completed draft whose version is behind the one we
  // just asked for, is still "generating" from the host's point of view
  // whenever a generation was actually triggered (Generate or Regenerate) —
  // the queue consumer just hasn't written its placeholder/replacement yet.
  // Computed before the "no suggestion yet" fallback below (not just inside
  // refetchInterval) so a null response right after a triggered generation
  // doesn't render an enabled "Generate review" button — a host reading
  // that as a failed click could fire off a duplicate generation (Codex
  // review on this PR).
  const targetVersion = queryClient.getQueryData<number>(targetVersionQueryKey) ?? 0
  const isGenerating =
    isSuggestionStillGenerating(suggestion, targetVersion) || regenerateMutation.isPending

  // Generation hasn't happened yet (or the reservation predates this
  // feature and hasn't been backfilled) — offer a manual trigger rather
  // than silently showing nothing. Not shown while isGenerating, so a null
  // result while waiting on a just-triggered generation falls through to
  // the "Generating…" branch below instead.
  if (!suggestion && !isGenerating) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-surface2/50 px-4 py-2.5">
        <span className="text-xs text-textMuted">No suggested guest review yet.</span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => regenerateMutation.mutate()}
          disabled={regenerateMutation.isPending}
        >
          <RefreshCw
            className={`mr-1.5 h-3.5 w-3.5 ${regenerateMutation.isPending ? 'animate-spin' : ''}`}
          />
          Generate review
        </Button>
      </div>
    )
  }

  if (isGenerating) {
    // Mirrors refetchInterval's own attemptsSinceGeneration check above (which
    // runs outside render, in React Query's scheduler) purely to decide which
    // fallback UI to show. notifyOnChangeProps: 'all' above ensures this
    // component re-renders on every fetch resolution, so the ref stays fresh
    // enough for this comparison in practice -- already reviewed (see Codex
    // review notes on this file for the exhausted-poll-budget UI).
    // eslint-disable-next-line react-hooks/refs
    const pollBaseline = pollBaselineRef.current
    const attemptsSinceGeneration =
      getFetchAttemptCount(queryClient.getQueryState(queryKey)) - pollBaseline
    const pollingExhausted = attemptsSinceGeneration >= MAX_ACTIVE_GENERATION_POLL_ATTEMPTS

    if (pollingExhausted && !regenerateMutation.isPending) {
      return (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-danger/30 bg-danger/5 px-4 py-2.5">
          <span className="text-xs text-danger">
            Still generating — this is taking longer than expected.
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              // Checks whether the existing generation actually finished
              // (or advanced closer to the target version) rather than
              // starting a brand new one — the job this block was waiting
              // on can still complete right after the polling budget runs
              // out, and page-level Refresh only refetches the digest, not
              // this per-reservation query, so without this the host had
              // no way to discover a late result short of a full reload,
              // and would likely just enqueue an unnecessary extra version
              // instead (Codex review on this PR). Resetting the baseline
              // gives it a fresh polling window in case it's still going.
              pollBaselineRef.current = getFetchAttemptCount(queryClient.getQueryState(queryKey))
              query.refetch()
            }}
          >
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            Check again
          </Button>
        </div>
      )
    }

    return (
      <div className="flex items-center gap-2 rounded-lg border border-mint/30 bg-mint/5 px-4 py-2.5">
        <Badge variant="secondary" className="animate-pulse">
          Generating…
        </Badge>
        <span className="text-xs text-textMuted">
          Drafting a suggested review for {guestName || 'this guest'}.
        </span>
      </div>
    )
  }

  if (suggestion?.status === 'failed') {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-danger/30 bg-danger/5 px-4 py-2.5">
        <span className="text-xs text-danger">
          Couldn&apos;t generate a suggested review
          {suggestion.error ? `: ${suggestion.error}` : '.'}
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => regenerateMutation.mutate()}
          disabled={regenerateMutation.isPending}
        >
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-2 rounded-lg border border-mint/30 bg-mint/10 px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-textMuted">Suggested guest review</span>
        {suggestion && suggestion.version > 1 && (
          <Badge variant="secondary" className="text-xs px-1.5 py-0 h-4">
            v{suggestion.version}
          </Badge>
        )}
      </div>
      <p className="text-sm leading-relaxed whitespace-pre-wrap select-text">
        {suggestion?.review_text}
      </p>
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <CopyButton text={suggestion?.review_text ?? ''} label="Copy review" />
        <Button
          size="sm"
          variant="outline"
          onClick={() => regenerateMutation.mutate()}
          disabled={regenerateMutation.isPending}
        >
          <RefreshCw
            className={`mr-1.5 h-3.5 w-3.5 ${regenerateMutation.isPending ? 'animate-spin' : ''}`}
          />
          Regenerate
        </Button>
      </div>
    </div>
  )
}
